/* eslint-disable @typescript-eslint/no-explicit-any */
'use client';

import { useEffect } from 'react';

declare global {
  interface Window {
    ['sourcepoint/utility']: {
      isVendorAllowed: (vendorName: string) => Promise<{
        allowed: boolean;
        gdprApplies: boolean;
        gdprAllowed: boolean;
        mspsApplies: boolean;
        mspsAllowed: boolean;
        ccpaApplies: boolean;
        ccpaAllowed: boolean;
      }>;
      isTcfAllowed: () => Promise<{
        allowed: boolean;
        gdprApplies: boolean;
        gdprAllowed: boolean;
      }>;
      isGppAllowed: () => Promise<{
        allowed: boolean;
        mspsApplies: boolean;
        mspsAllowed: boolean;
      }>;
    };
    __tcfapi?: (
      command: string,
      version: number,
      callback: (tcData: any, success: boolean) => void,
      parameter?: string | number
    ) => void;
    fbq?: (arg1: string, arg2: string, ...rest: any[]) => void;
    _fbq?: any;
    thirdPartyDataLayer?: any[];
    gtag?: (...args: any[]) => void;
    dataLayer?: any[];
    _hsq?: any[];
    _linkedin_partner_id?: string;
    _linkedin_data_partner_ids?: string[];
    lintrk?: (a: string, b: any) => void | { q: Array<[string, any]> };
  }
}

type VendorScripts = {
  [key: string]: {
    scriptId: string;
    src?: string;
    inline?: string;
    onLoad?: () => void;
  };
};

const VENDOR_SCRIPTS: VendorScripts = {
  hotjar: {
    scriptId: 'hotjar-script',
    inline: `
    (function(h,o,t,j,a,r){
      h.hj=h.hj||function(){(h.hj.q=h.hj.q||[]).push(arguments)};
      h._hjSettings={hjid:870031,hjsv:6};
      a=o.getElementsByTagName('head')[0];
      r=o.createElement('script');r.async=1;
      r.src=t+h._hjSettings.hjid+j+h._hjSettings.hjsv;
      a.appendChild(r);
    })(window,document,'https://static.hotjar.com/c/hotjar-','.js?sv=');
    `,
  },
  bing: {
    scriptId: 'bing-script',
    inline: `
    (function (w, d, t, r, u) {
      var f, n, i;
      (w[u] = w[u] || []),
        (f = function () {
          var o = { ti: "148006416" };
          (o.q = w[u]), (w[u] = new UET(o)), w[u].push("pageLoad");
        }),
        (n = d.createElement(t)),
        (n.src = r),
        (n.async = 1),
        (n.onload = n.onreadystatechange =
          function () {
            var s = this.readyState;
            (s && s !== "loaded" && s !== "complete") ||
              (f(), (n.onload = n.onreadystatechange = null));
          }),
        (i = d.getElementsByTagName(t)[0]),
        i.parentNode.insertBefore(n, i);
    })(window, document, "script", "//bat.bing.com/bat.js", "uetq");
    // enable TCF
    window.uetq = window.uetq || [];
    window.uetq.push("config", "tcf", { "enabled" : true });
    `,
  },
  capterra: {
    scriptId: 'capterra-script',
    inline: `
    (function() {
      if (!document.getElementById('capterra-tracking-img')) {
        const capterraImg = document.createElement("img");
        capterraImg.id = 'capterra-tracking-img';
        capterraImg.src = "https://ct.capterra.com/capterra_tracker.gif?vid=2095332&vkey=eea96217aab93b4cd8f88d664ac3c6e5";
        capterraImg.style.display = 'none';
        const span = document.createElement("span");
        span.appendChild(capterraImg);
        document.body.appendChild(span);
      }
    })();
    `,
  },
  convert: {
    scriptId: 'convert-script',
    inline: `
    (function (d) {
      var s = d.createElement("script");
      s.type = "text/javascript";
      s.src = "//cdn-4.convertexperiments.com/js/10042341-10042997.js";
      d.getElementsByTagName("head")[0].appendChild(s);
    })(document);
    `,
  },
  facebook: {
    scriptId: 'facebook-pixel-script',
    inline: `
    !function(f,b,e,v,n,t,s){if(f.fbq)return;n=f.fbq=function(){n.callMethod?
    n.callMethod.apply(n,arguments):n.queue.push(arguments)};if(!f._fbq)f._fbq=n;
    n.push=n;n.loaded=!0;n.version='2.0';n.queue=[];t=b.createElement(e);t.async=!0;
    t.src=v;s=b.getElementsByTagName(e)[0];s.parentNode.insertBefore(t,s)}(window,
    document,'script','https://connect.facebook.net/en_US/fbevents.js');
    fbq('init', '1696408607309042');
    fbq('track', 'PageView');

    (function() {
      if (!document.getElementById('fb-pixel-img')) {
        const fbImg = document.createElement('img');
        fbImg.id = 'fb-pixel-img';
        fbImg.height = 1;
        fbImg.width = 1;
        fbImg.style.display = 'none';
        fbImg.src = 'https://www.facebook.com/tr?id=1696408607309042&ev=PageView&noscript=1';
        document.body.appendChild(fbImg);
      }
    })();
    `,
  },
  googleAds: {
    scriptId: 'google-ads-script',
    src: 'https://www.googletagmanager.com/gtag/js?id=AW-877246914&l=thirdPartyDataLayer',
    inline: `
    window.thirdPartyDataLayer = window.thirdPartyDataLayer || [];
    function gtag(){thirdPartyDataLayer.push(arguments);}
    gtag('js', new Date());
    gtag('config', 'AW-877246914');
    `,
  },
  googleAnalytics: {
    scriptId: 'google-tag-manager-script',
    inline: `
    (function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
    new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
    j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
    'https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);
    })(window,document,'script','dataLayer','GTM-T6PDSHH');`,
  },
  hubspot: {
    scriptId: 'hubspot-script',
    src: '//js.hs-scripts.com/7088586.js?businessUnitId=33104',
    inline: `
    var _hsq = window._hsq = window._hsq || [];
    `,
  },
  linkedIn: {
    scriptId: 'linkedin-script',
    inline: `
    _linkedin_partner_id = "3708418";
    window._linkedin_data_partner_ids = window._linkedin_data_partner_ids || [];
    window._linkedin_data_partner_ids.push(_linkedin_partner_id);
    
    (function(l) {
      if (!l){window.lintrk = function(a,b){window.lintrk.q.push([a,b])};
      window.lintrk.q=[]}
      var s = document.getElementsByTagName("script")[0];
      var b = document.createElement("script");
      b.type = "text/javascript";b.async = true;
      b.src = "https://snap.licdn.com/li.lms-analytics/insight.min.js";
      s.parentNode.insertBefore(b, s);
    })(window.lintrk);

    (function() {
      if (!document.getElementById('linkedin-tracking-img')) {
        const linkedInImg = document.createElement("img");
        linkedInImg.id = 'linkedin-tracking-img';
        linkedInImg.height = 1;
        linkedInImg.width = 1;
        linkedInImg.style.display = "none";
        linkedInImg.alt = "";
        linkedInImg.src = "https://px.ads.linkedin.com/collect/?pid=3708418&fmt=gif";
        document.body.appendChild(linkedInImg);
      }
    })();
    `,
  },
  optInMonster: {
    scriptId: 'optin-monster-script',
    inline: `
    (function (d, u, ac) {
      var s = d.createElement("script");
      s.type = "text/javascript";
      s.src = "https://a.omappapi.com/app/js/api.min.js";
      s.async = true;
      s.dataset.user = u;
      s.dataset.account = ac;
      d.getElementsByTagName("head")[0].appendChild(s);
    })(document, 126907, 139257);
    `,
  },
  // Add other vendor scripts here
};

export default function ConsentManager() {
  useEffect(() => {
    // Function to handle vendor scripts based on consent
    const handleVendorScripts = async () => {
      for (const vendorName of Object.keys(VENDOR_SCRIPTS)) {
        try {
          const vendorConsent =
            await window['sourcepoint/utility'].isVendorAllowed(vendorName);
          const scriptConfig = VENDOR_SCRIPTS[vendorName];
          if (vendorConsent.allowed) {
            addScript(scriptConfig);
          } else {
            removeScript(scriptConfig.scriptId);
          }
        } catch {}
      }
    };

    const handleConsentChange = () => {
      if (typeof window.__tcfapi === 'function') {
        // Handle initial consent state
        window.__tcfapi('getTCData', 2, async (tcData, success) => {
          if (success) {
            await handleVendorScripts();
          }
        });

        // Listen for consent changes
        window.__tcfapi('addEventListener', 2, async (tcData, success) => {
          if (success) {
            if (
              tcData.eventStatus === 'useractioncomplete' ||
              tcData.eventStatus === 'tcloaded'
            ) {
              await handleVendorScripts();
            }
          }
        });
      }
    };

    const addScript = (scriptConfig: VendorScripts[keyof VendorScripts]) => {
      // Remove existing scripts if any
      removeScript(scriptConfig.scriptId);
      removeScript(`${scriptConfig.scriptId}-inline`);

      // If external script exists, add it first
      if (scriptConfig.src) {
        const externalScript = document.createElement('script');
        externalScript.id = scriptConfig.scriptId;
        externalScript.src = scriptConfig.src;
        externalScript.async = true;

        // Only add inline script after external script loads
        externalScript.onload = () => {
          // Add inline script if it exists
          if (scriptConfig.inline) {
            const inlineScript = document.createElement('script');
            inlineScript.id = `${scriptConfig.scriptId}-inline`;
            inlineScript.textContent = scriptConfig.inline;

            inlineScript.onload = () => {
              if (scriptConfig.onLoad) {
                scriptConfig.onLoad();
              }
            };

            document.head.appendChild(inlineScript);
          }
        };

        document.head.appendChild(externalScript);
      } else if (scriptConfig.inline) {
        // If only inline script exists

        const script = document.createElement('script');
        script.id = scriptConfig.scriptId;
        script.textContent = scriptConfig.inline;

        script.onload = () => {
          if (scriptConfig.onLoad) {
            scriptConfig.onLoad();
          }
        };

        document.head.appendChild(script);
      }
    };

    const removeScript = (scriptId: string) => {
      const existingScript = document.getElementById(scriptId);
      if (existingScript) {
        existingScript.remove();
      }
    };

    // Wait for sourcePointUtils to be available
    const waitForDependencies = () => {
      const initInterval = setInterval(() => {
        if (
          window['sourcepoint/utility'] &&
          typeof window.__tcfapi === 'function'
        ) {
          clearInterval(initInterval);
          handleConsentChange();
        }
      }, 500);

      return initInterval;
    };

    const initInterval = waitForDependencies();

    return () => {
      clearInterval(initInterval);
      if (typeof window.__tcfapi === 'function') {
        window.__tcfapi('removeEventListener', 2, () => {}, 1);
      }
    };
  }, []);

  return null;
}
